<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const route = useRoute();
const router = useRouter();
const contactId = route.params.id;

const contact = ref(null);
const notes = ref([]);
const tasks = ref([]);
const deals = ref([]);
const activities = ref([]);
const activity = ref([]);
const showActivityModal = ref(false);
const guardandoActividad = ref(false);
const activityForm = ref({ type: "llamada", startsAt: "", endsAt: "", result: "", notes: "" });
const allTags = ref([]);
const contactTags = ref([]);
const customFieldDefs = ref([]);
const cargando = ref(true);
const guardandoNota = ref(false);
const nuevaNota = ref("");

const showEditModal = ref(false);
const showTagsModal = ref(false);
const showCustomFieldsModal = ref(false);
const guardando = ref(false);

const editForm = ref({});
const tagsSeleccionados = ref([]);
const customFieldValues = ref({});

const getContact = async () => {
  const res = await api.get(`/crm/contacts/${contactId}`);
  contact.value = res.data.contact;
};

const getNotes = async () => {
  const res = await api.get(`/crm/contacts/${contactId}/notes`);
  notes.value = res.data.notes;
};

const getTasks = async () => {
  const res = await api.get("/crm/tasks");
  tasks.value = res.data.tasks.filter(t => t.contact_id === Number(contactId));
};

const getDeals = async () => {
  const res = await api.get("/crm/deals", { params: { contactId } });
  deals.value = res.data.deals;
};

const getActivity = async () => {
  const res = await api.get(`/crm/contacts/${contactId}/activity`);
  activity.value = res.data.activity;
};

const getActivities = async () => {
  const res = await api.get("/crm/activities", { params: { contactId } });
  activities.value = res.data.activities;
};

const NOMBRE_TIPO_ACTIVIDAD = {
  llamada: "Llamada", correo: "Correo", whatsapp: "WhatsApp", reunion: "Reunión",
  videollamada: "Videollamada", visita: "Visita", seguimiento: "Seguimiento"
};

const abrirNuevaActividad = () => {
  const ahora = new Date();
  ahora.setMinutes(ahora.getMinutes() - ahora.getTimezoneOffset());
  activityForm.value = { type: "llamada", startsAt: ahora.toISOString().slice(0, 16), endsAt: "", result: "", notes: "" };
  showActivityModal.value = true;
};

const guardarActividad = async () => {
  if (!activityForm.value.startsAt) {
    Swal.fire({ icon: "warning", title: "La fecha/hora de inicio es obligatoria" });
    return;
  }

  guardandoActividad.value = true;

  try {
    await api.post("/crm/activities", { ...activityForm.value, contactId });
    showActivityModal.value = false;
    await Promise.all([getActivities(), getActivity()]);
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar la actividad",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardandoActividad.value = false;
  }
};

const getAllTags = async () => {
  const res = await api.get("/crm/tags");
  allTags.value = res.data.tags;
};

const getContactTags = async () => {
  const res = await api.get(`/crm/contacts/${contactId}/tags`);
  contactTags.value = res.data.tags;
};

const getCustomFieldDefs = async () => {
  const res = await api.get("/crm/custom-fields");
  customFieldDefs.value = res.data.fields;
};

const formatFecha = (fecha) => new Date(fecha).toLocaleString("es-CO");

const formatFechaCorta = (fecha) => {
  if (!fecha) return "Sin fecha";
  return new Date(fecha).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
};

const tareaVencida = (task) => !task.completed_at && task.due_at && new Date(task.due_at) < new Date();

function formatMoneda(valor) {
  return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(valor || 0);
}

// El color de cada etiqueta lo elige el usuario (admin), así que puede ser
// claro u oscuro -- se calcula el color de texto con más contraste en vez
// de asumir blanco fijo (que se vuelve invisible sobre colores claros).
function colorTextoContraste(hexFondo) {
  if (!hexFondo) return "#fff";
  const hex = hexFondo.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brillo = (r * 299 + g * 587 + b * 114) / 1000;
  return brillo > 150 ? "#1a1a2e" : "#fff";
}

const iconoActividad = (type) => ({
  contact_created: "bi-person-plus",
  note_added: "bi-sticky",
  deal_created: "bi-kanban",
  deal_stage_changed: "bi-arrow-left-right",
  contact_updated: "bi-pencil",
  message_sent: "bi-send",
  activity_logged: "bi-calendar-check"
}[type] || "bi-clock-history");

const iconoTipoActividad = (type) => ({
  llamada: "bi-telephone", correo: "bi-envelope", whatsapp: "bi-whatsapp", reunion: "bi-people",
  videollamada: "bi-camera-video", visita: "bi-geo-alt", seguimiento: "bi-flag"
}[type] || "bi-calendar-event");

const completarTarea = async (task) => {
  await api.patch(`/crm/tasks/${task.id}/complete`);
  await getTasks();
};

const agregarNota = async () => {
  if (!nuevaNota.value.trim()) return;

  guardandoNota.value = true;

  try {
    await api.post(`/crm/contacts/${contactId}/notes`, { body: nuevaNota.value });
    nuevaNota.value = "";
    await Promise.all([getNotes(), getActivity()]);
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar la nota",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardandoNota.value = false;
  }
};

const abrirEditar = () => {
  editForm.value = {
    name: contact.value.name || "",
    lastName: contact.value.last_name || "",
    email: contact.value.email || "",
    phone: contact.value.phone || "",
    whatsapp: contact.value.whatsapp || "",
    instagram: contact.value.instagram || "",
    facebook: contact.value.facebook || "",
    linkedin: contact.value.linkedin || "",
    position: contact.value.position || "",
    source: contact.value.source || "",
    birthDate: contact.value.birth_date ? contact.value.birth_date.slice(0, 10) : "",
    address: contact.value.address || "",
    city: contact.value.city || "",
    country: contact.value.country || ""
  };
  showEditModal.value = true;
};

const guardarEdicion = async () => {
  guardando.value = true;

  try {
    const res = await api.put(`/crm/contacts/${contactId}`, editForm.value);
    contact.value = res.data.contact;
    showEditModal.value = false;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo actualizar el contacto",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

const abrirTags = () => {
  tagsSeleccionados.value = contactTags.value.map(t => t.id);
  showTagsModal.value = true;
};

const toggleTag = (tagId) => {
  const idx = tagsSeleccionados.value.indexOf(tagId);
  if (idx === -1) tagsSeleccionados.value.push(tagId);
  else tagsSeleccionados.value.splice(idx, 1);
};

const guardarTags = async () => {
  guardando.value = true;

  try {
    await api.put(`/crm/contacts/${contactId}/tags`, { tagIds: tagsSeleccionados.value });
    await getContactTags();
    showTagsModal.value = false;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron guardar las etiquetas",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

const abrirCustomFields = () => {
  customFieldValues.value = { ...contact.value.custom_fields };
  showCustomFieldsModal.value = true;
};

const guardarCustomFields = async () => {
  guardando.value = true;

  try {
    const res = await api.put(`/crm/contacts/${contactId}`, { customFields: customFieldValues.value });
    contact.value = res.data.contact;
    showCustomFieldsModal.value = false;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron guardar los campos personalizados",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

const iniciarChat = (channel) => {
  router.push(`/crm/inbox?contactId=${contactId}&channel=${channel}`);
};

const irAlEmbudo = () => router.push("/crm/pipeline");
const volver = () => router.push("/crm/contacts");

onMounted(async () => {
  try {
    await Promise.all([
      getContact(), getNotes(), getTasks(), getDeals(), getActivity(), getActivities(),
      getAllTags(), getContactTags(), getCustomFieldDefs()
    ]);
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el contacto",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0" style="color: white;">
        <i class="bi bi-person-lines-fill me-2"></i>
        Ficha de contacto
      </h4>

      <argon-button color="secondary" @click="volver">
        <i class="bi bi-arrow-left me-1"></i>
        Volver a Contactos
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else-if="contact" class="row">

      <!-- INFO DEL CONTACTO -->
      <div class="col-md-4">
        <div class="card mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <h5>{{ contact.name }} {{ contact.last_name || "" }}</h5>
              <button class="btn btn-sm btn-light" @click="abrirEditar">
                <i class="bi bi-pencil"></i>
              </button>
            </div>

            <p v-if="contact.position" class="text-muted small mb-2">{{ contact.position }}</p>

            <p class="mb-1"><i class="bi bi-telephone me-2"></i>{{ contact.phone || "Sin teléfono" }}</p>
            <p class="mb-1"><i class="bi bi-whatsapp me-2"></i>{{ contact.whatsapp || "—" }}</p>
            <p class="mb-1"><i class="bi bi-envelope me-2"></i>{{ contact.email || "Sin email" }}</p>
            <p v-if="contact.crm_company_name" class="mb-1">
              <i class="bi bi-building me-2"></i>{{ contact.crm_company_name }}
            </p>
            <p v-if="contact.city || contact.country" class="mb-1">
              <i class="bi bi-geo-alt me-2"></i>{{ [contact.city, contact.country].filter(Boolean).join(", ") }}
            </p>
            <p class="mb-2"><i class="bi bi-tag me-2"></i>Origen: {{ contact.source || "—" }}</p>

            <div class="d-flex gap-2 mb-3">
              <a v-if="contact.instagram" :href="`https://instagram.com/${contact.instagram}`" target="_blank" class="btn btn-sm btn-outline-danger">
                <i class="bi bi-instagram"></i>
              </a>
              <a v-if="contact.facebook" :href="contact.facebook" target="_blank" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-facebook"></i>
              </a>
              <a v-if="contact.linkedin" :href="contact.linkedin" target="_blank" class="btn btn-sm btn-outline-info">
                <i class="bi bi-linkedin"></i>
              </a>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="small text-muted mb-0">Etiquetas</label>
                <button class="btn btn-sm btn-link p-0" @click="abrirTags">Editar</button>
              </div>
              <div class="d-flex flex-wrap gap-1">
                <span v-for="tag in contactTags" :key="tag.id" class="badge" :style="{ backgroundColor: tag.color, color: colorTextoContraste(tag.color) }">
                  {{ tag.name }}
                </span>
                <span v-if="contactTags.length === 0" class="text-muted small">Sin etiquetas</span>
              </div>
            </div>

            <div class="mb-3" v-if="customFieldDefs.length > 0">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="small text-muted mb-0">Campos personalizados</label>
                <button class="btn btn-sm btn-link p-0" @click="abrirCustomFields">Editar</button>
              </div>
              <div v-for="field in customFieldDefs" :key="field.id" class="small mb-1">
                <strong>{{ field.label }}:</strong> {{ contact.custom_fields?.[field.key] || "—" }}
              </div>
            </div>

            <label class="d-block mb-1">Iniciar chat</label>
            <div class="d-flex gap-2">
              <button class="btn btn-outline-success btn-sm" @click="iniciarChat('whatsapp')">
                <i class="bi bi-whatsapp me-1"></i>WhatsApp
              </button>
              <button class="btn btn-outline-danger btn-sm" @click="iniciarChat('instagram')">
                <i class="bi bi-instagram me-1"></i>Instagram
              </button>
            </div>
          </div>
        </div>

        <!-- NEGOCIOS -->
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="bi bi-kanban me-1"></i>Negocios</h6>
            <button class="btn btn-sm btn-link p-0" @click="irAlEmbudo">Ir al embudo</button>
          </div>
          <div class="card-body">
            <div v-if="deals.length === 0" class="text-muted small">Sin negocios para este contacto.</div>
            <ul class="list-group" v-else>
              <li v-for="deal in deals" :key="deal.id" class="list-group-item">
                <div class="d-flex justify-content-between">
                  <strong>{{ deal.title }}</strong>
                  <span class="text-success small fw-bold">{{ formatMoneda(deal.value) }}</span>
                </div>
                <span class="badge bg-gradient-secondary">{{ deal.stage_name }}</span>
                <div v-if="deal.loss_reason_name" class="small text-danger mt-1">
                  <i class="bi bi-x-octagon me-1"></i>{{ deal.loss_reason_name }}
                  <span v-if="deal.loss_note">— {{ deal.loss_note }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-md-8">

        <!-- TAREAS -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-check2-square me-1"></i>Tareas</h6>
          </div>

          <div class="card-body">
            <div v-if="tasks.length === 0" class="text-muted small">
              Sin tareas para este contacto.
            </div>

            <ul class="list-group" v-else>
              <li
                v-for="task in tasks"
                :key="task.id"
                class="list-group-item d-flex justify-content-between align-items-center"
                :class="{ 'list-group-item-danger': tareaVencida(task) }"
              >
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="!!task.completed_at"
                    :disabled="!!task.completed_at"
                    @change="completarTarea(task)"
                  >
                  <label class="form-check-label" :class="{ 'text-decoration-line-through text-muted': task.completed_at }">
                    {{ task.title }}
                  </label>
                </div>
                <small :class="tareaVencida(task) ? 'text-danger fw-bold' : 'text-muted'">
                  {{ task.completed_at ? 'Completada' : formatFechaCorta(task.due_at) }}
                </small>
              </li>
            </ul>
          </div>
        </div>

        <!-- ACTIVIDADES -->
        <div class="card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="bi bi-calendar-check me-1"></i>Actividades</h6>
            <button class="btn btn-sm btn-link p-0" @click="abrirNuevaActividad">+ Registrar</button>
          </div>
          <div class="card-body">
            <div v-if="activities.length === 0" class="text-muted small">Sin actividades registradas.</div>
            <ul class="list-group" v-else>
              <li v-for="act in activities" :key="act.id" class="list-group-item d-flex gap-2">
                <i class="bi mt-1" :class="iconoTipoActividad(act.type)"></i>
                <div class="flex-grow-1">
                  <div>
                    <strong>{{ NOMBRE_TIPO_ACTIVIDAD[act.type] || act.type }}</strong>
                    <span v-if="act.result" class="text-muted"> — {{ act.result }}</span>
                  </div>
                  <div v-if="act.notes" class="small">{{ act.notes }}</div>
                  <small class="text-muted">{{ act.user_name || "—" }} · {{ formatFecha(act.starts_at) }}</small>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- NOTAS -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">Notas / interacciones</h6>
          </div>

          <div class="card-body">

            <div class="mb-3 d-flex">
              <textarea
                v-model="nuevaNota"
                class="form-control me-2"
                rows="2"
                placeholder="Escribe una nota sobre este contacto..."
              ></textarea>

              <argon-button color="success" :disabled="guardandoNota" @click="agregarNota">
                <i class="bi bi-send"></i>
              </argon-button>
            </div>

            <div v-if="notes.length === 0" class="text-muted">
              Todavía no hay notas para este contacto.
            </div>

            <ul class="list-group" v-else>
              <li v-for="note in notes" :key="note.id" class="list-group-item">
                <div class="d-flex justify-content-between">
                  <strong>{{ note.author_name || "Usuario" }}</strong>
                  <small class="text-muted">{{ formatFecha(note.createdAt) }}</small>
                </div>
                <div>{{ note.body }}</div>
              </li>
            </ul>

          </div>
        </div>

        <!-- HISTORIAL DE ACTIVIDAD -->
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-clock-history me-1"></i>Historial de actividad</h6>
          </div>
          <div class="card-body">
            <div v-if="activity.length === 0" class="text-muted small">Sin actividad registrada todavía.</div>
            <ul class="list-group" v-else>
              <li v-for="ev in activity" :key="ev.id" class="list-group-item d-flex gap-2">
                <i class="bi mt-1" :class="iconoActividad(ev.type)"></i>
                <div class="flex-grow-1">
                  <div>{{ ev.description }}</div>
                  <small class="text-muted">{{ ev.author_name || "Sistema" }} — {{ formatFecha(ev.createdAt) }}</small>
                </div>
              </li>
            </ul>
          </div>
        </div>

      </div>

    </div>

    <!-- MODAL EDITAR CONTACTO -->
    <div v-if="showEditModal" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Editar contacto</h5>
            <button class="btn-close" @click="showEditModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small">Nombre</label>
                <argon-input v-model="editForm.name" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Apellido</label>
                <argon-input v-model="editForm.lastName" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Teléfono</label>
                <argon-input v-model="editForm.phone" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">WhatsApp</label>
                <argon-input v-model="editForm.whatsapp" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Email</label>
                <argon-input v-model="editForm.email" type="email" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Cargo</label>
                <argon-input v-model="editForm.position" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">Instagram</label>
                <argon-input v-model="editForm.instagram" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">Facebook</label>
                <argon-input v-model="editForm.facebook" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">LinkedIn</label>
                <argon-input v-model="editForm.linkedin" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">Fecha de nacimiento</label>
                <argon-input v-model="editForm.birthDate" type="date" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">Ciudad</label>
                <argon-input v-model="editForm.city" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">País</label>
                <argon-input v-model="editForm.country" />
              </div>
              <div class="col-12">
                <label class="form-label small">Dirección</label>
                <argon-input v-model="editForm.address" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showEditModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarEdicion">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showEditModal" class="modal-backdrop fade show"></div>

    <!-- MODAL ETIQUETAS -->
    <div v-if="showTagsModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Etiquetas</h5>
            <button class="btn-close" @click="showTagsModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="allTags.length === 0" class="text-muted small">
              No hay etiquetas creadas todavía (se gestionan desde el módulo de Embudo).
            </div>
            <div v-for="tag in allTags" :key="tag.id" class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="tagsSeleccionados.includes(tag.id)"
                @change="toggleTag(tag.id)"
              >
              <label class="form-check-label">
                <span class="badge me-1" :style="{ backgroundColor: tag.color, color: colorTextoContraste(tag.color) }">{{ tag.name }}</span>
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showTagsModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarTags">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showTagsModal" class="modal-backdrop fade show"></div>

    <!-- MODAL CAMPOS PERSONALIZADOS -->
    <div v-if="showCustomFieldsModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Campos personalizados</h5>
            <button class="btn-close" @click="showCustomFieldsModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-for="field in customFieldDefs" :key="field.id" class="mb-2">
              <label class="form-label small">{{ field.label }}</label>
              <argon-input
                v-model="customFieldValues[field.key]"
                :type="field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showCustomFieldsModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarCustomFields">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showCustomFieldsModal" class="modal-backdrop fade show"></div>

    <!-- MODAL NUEVA ACTIVIDAD -->
    <div v-if="showActivityModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Registrar actividad</h5>
            <button class="btn-close" @click="showActivityModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-2">
              <label class="form-label small">Tipo</label>
              <select v-model="activityForm.type" class="form-select">
                <option v-for="(label, value) in NOMBRE_TIPO_ACTIVIDAD" :key="value" :value="value">{{ label }}</option>
              </select>
            </div>
            <div class="row g-2">
              <div class="col-6">
                <label class="form-label small">Inicio</label>
                <argon-input v-model="activityForm.startsAt" type="datetime-local" />
              </div>
              <div class="col-6">
                <label class="form-label small">Fin</label>
                <argon-input v-model="activityForm.endsAt" type="datetime-local" />
              </div>
            </div>
            <div class="mt-2">
              <label class="form-label small">Resultado</label>
              <argon-input v-model="activityForm.result" placeholder="Interesado, no contestó, etc." />
            </div>
            <div class="mt-2">
              <label class="form-label small">Notas</label>
              <textarea v-model="activityForm.notes" class="form-control" rows="2"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showActivityModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardandoActividad" @click="guardarActividad">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showActivityModal" class="modal-backdrop fade show"></div>

  </div>
</template>
